/**
 * 旧PCRM のボタン（crm/frontend/src/index.css の .btn・.btn-primary・.btn-secondary・.btn-success・.btn-danger）の見た目（262 r2）。
 * 全体のテーマ（257：ボタンは全部オレンジ）は変えず、旧に合わせる画面のボタンにだけ sx で付ける。
 * - 形：高さ 32px・左右 12px・角 6px・13px・太さ 500（旧のスクショで実測：2 文字のボタンが幅 50px・高さ 32px）
 * - 色：primary #ea580c（乗せたとき #c2410c）、secondary は白地・#ddd の枠・#333 の文字（277：旧の実測）、
 *   success #10b981（#059669）、danger #dc2626（#b91c1c）
 * - warning（273：フォームの「アーカイブ」）：旧は .btn-warning を付けていたが CSS に定義が無く、ブラウザ既定のボタン色
 *   （灰 #efefef・黒文字）で出ていた。その見た目に合わせる
 */
import type { SxProps, Theme } from '@mui/material';

export type LegacyButtonKind = 'primary' | 'secondary' | 'success' | 'danger' | 'warning';

export const LEGACY_BUTTON_BASE_SX = {
  minWidth: 0,
  minHeight: 32,
  height: 32,
  px: '12px',
  py: '6px',
  borderRadius: '6px',
  fontSize: 13,
  fontWeight: 500,
  // 277：旧 .btn は line-height 1（13px）
  lineHeight: 1,
  boxShadow: 'none',
  textTransform: 'none',
  '& .MuiButton-startIcon': { mr: '5px', ml: 0 },
  '& .MuiButton-endIcon': { ml: '5px', mr: 0 },
  '&.Mui-disabled': { opacity: 0.5 },
} as const;

const KIND_SX: Readonly<Record<LegacyButtonKind, Record<string, unknown>>> = {
  primary: {
    bgcolor: '#ea580c',
    color: '#ffffff',
    border: 'none',
    '&:hover': { bgcolor: '#c2410c', boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: '#ea580c', color: '#ffffff', opacity: 0.5 },
  },
  secondary: {
    bgcolor: '#ffffff',
    // 277：旧の実測（白地・文字 #333・枠 #ddd）。キャンセル・すべて展開・進捗を記録など
    color: '#333333',
    border: '1px solid #dddddd',
    '&:hover': { bgcolor: '#f1f5f9', borderColor: '#cbd5e1', boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: '#ffffff', color: '#333333', borderColor: '#dddddd', opacity: 0.5 },
  },
  success: {
    bgcolor: '#10b981',
    color: '#ffffff',
    border: 'none',
    '&:hover': { bgcolor: '#059669', boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: '#10b981', color: '#ffffff', opacity: 0.5 },
  },
  danger: {
    bgcolor: '#dc2626',
    color: '#ffffff',
    border: 'none',
    '&:hover': { bgcolor: '#b91c1c', boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: '#dc2626', color: '#ffffff', opacity: 0.5 },
  },
  warning: {
    bgcolor: '#efefef',
    color: '#000000',
    border: 'none',
    '&:hover': { bgcolor: '#e5e5e5', boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: '#efefef', color: '#000000', opacity: 0.5 },
  },
};

/** 旧の .btn ＋ 色（例：<Button variant="contained" sx={legacyButtonSx('primary')}>） */
export function legacyButtonSx(kind: LegacyButtonKind): SxProps<Theme> {
  return { ...LEGACY_BUTTON_BASE_SX, ...KIND_SX[kind] };
}
